import type { DataStreamScope } from '@squaredup/data-streams';
import LoadingSpinner from 'components/LoadingSpinner';
import type { PartialDeep } from 'type-fest';
import { EditorSteps } from '../../../constants';
import { useObjectFilterObjects } from '../../../hooks/objectFilters/useObjectFilterObjects';
import { useScopeBaseQuery } from '../../../hooks/useDataStreamObjectFilters';
import type { StepWithCondition } from '../../../hooks/useTileEditorSteps';
import type { ScopeState } from '../../../state/scope/ScopeState';
import type { TileEditorDataStream } from '../../../state/TileEditorState';
import { DataStreamObjectsSublabel } from './DataStreamObjectsSublabel';
import { ObjectsStep } from './ObjectsStep';

interface ObjectsSublabelProps {
    hasDataStream: boolean;
    isFetchingObjects: boolean;
    storeCurrentStep: EditorSteps;
    datasetConfigScope?: PartialDeep<DataStreamScope>;
    scopeState: ScopeState;
    workspaceId: string;
}

interface UseObjectsStepParameters {
    supportsScope: boolean;
    isLoading: boolean;
    hasDataStream: boolean;
    storeCurrentStep: EditorSteps;
    datasetConfigScope?: PartialDeep<DataStreamScope>;
    scopeState: ScopeState;
    workspaceId: string;
    selectedDataStream: TileEditorDataStream | undefined;
}

const ObjectsSublabel: React.FC<ObjectsSublabelProps> = ({
    hasDataStream,
    isFetchingObjects,
    storeCurrentStep,
    datasetConfigScope,
    scopeState,
    workspaceId
}) => {
    if (!hasDataStream) {
        return <>-</>;
    }

    if (isFetchingObjects && storeCurrentStep !== EditorSteps.objects && !datasetConfigScope) {
        return <LoadingSpinner size={24} />;
    }

    return (
        <DataStreamObjectsSublabel
            scopeState={scopeState}
            scopeConfig={datasetConfigScope ?? []}
            workspaceId={workspaceId}
        />
    );
};

/**
 * Create an object representing the objects step.
 */
export const useObjectsStep = ({
    supportsScope,
    isLoading,
    hasDataStream,
    storeCurrentStep,
    datasetConfigScope,
    scopeState,
    workspaceId,
    selectedDataStream
}: UseObjectsStepParameters): StepWithCondition<ObjectsSublabelProps> => {
    const { scopeBaseQuery, isLoading: isLoadingScopeBaseQuery } = useScopeBaseQuery(selectedDataStream, undefined);

    const {
        isFetchingObjects,
        isPreviousData,
        count: objectCount
    } = useObjectFilterObjects({
        scopeBaseQuery,
        queryParams: {},
        isFilterQueryReady: !isLoadingScopeBaseQuery && selectedDataStream != null && !scopeState.isDynamic,
        options: { keepPreviousData: false }
    });

    const isEnabled =
        supportsScope &&
        (scopeState.isDynamic ||
            storeCurrentStep === EditorSteps.objects ||
            (!isLoading &&
                !isPreviousData &&
                hasDataStream &&
                objectCount != null &&
                // We might be about to auto-select if we're still loading objects
                !(isFetchingObjects && scopeState.type === 'none') &&
                !(
                    !isFetchingObjects &&
                    scopeState.type === 'objectList' &&
                    objectCount === 1 &&
                    scopeState.selectedNodeIds.length === 1
                )));

    return {
        name: EditorSteps.objects,
        isEnabled,
        Sublabel: ObjectsSublabel,
        sublabelProps: {
            hasDataStream,
            isFetchingObjects,
            storeCurrentStep,
            datasetConfigScope,
            scopeState,
            workspaceId
        },
        Content: ObjectsStep,
        contentProps: {}
    };
};
